
import { useQueueStore } from "@/lib/store";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Clock, CheckCircle, AlertTriangle } from "lucide-react";
import { Patient } from "@/types";

// Toggle this flag to switch between colored card backgrounds and neutral backgrounds
const useColoredCards = true; // Set to false to revert to present style

interface QueueSummaryProps {
  patients?: Patient[];
}

export function QueueSummary({ patients }: QueueSummaryProps) {
  const queueStore = useQueueStore();
  // Use provided patients or fall back to all patients from store
  const displayPatients = patients || queueStore.patients;
  
  const waitingCount = displayPatients.filter((p) => p.status === "waiting").length;
  const inConsultationCount = displayPatients.filter((p) => p.status === "in-consultation").length;
  const completedCount = displayPatients.filter((p) => p.status === "completed").length;
  // Count both "urgent" and "emergency" priority patients (excluding completed)
  const urgentCount = displayPatients.filter((p) => 
    (p.priority === "urgent" || p.priority === "emergency") && p.status !== "completed"
  ).length;
  
  const items = [
    {
      title: "Waiting",
      value: waitingCount,
      icon: Users,
      color: "text-yellow-800",
      bgColor: "bg-yellow-100",
    },
    {
      title: "In Consultation",
      value: inConsultationCount,
      icon: Clock,
      color: "text-blue-800",
      bgColor: "bg-blue-100",
    },
    {
      title: "Completed",
      value: completedCount,
      icon: CheckCircle,
      color: "text-green-800",
      bgColor: "bg-green-100",
    },
    {
      title: "Emergency Cases",
      value: urgentCount,
      icon: AlertTriangle,
      color: "text-red-800",
      bgColor: "bg-red-100",
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {items.map((item) => (
        <Card
          key={item.title}
          className={
            useColoredCards
              ? `${item.bgColor} border-0 shadow-sm`
              : ""
          }
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              {/* Left side: Icon and Title */}
              <div className="flex items-center gap-3">
                <div
                  className={
                    useColoredCards
                      ? `p-2 rounded-full bg-white/70`
                      : `${item.bgColor} p-2 rounded-full`
                  }
                >
                  <item.icon className={`h-4 w-4 ${item.color}`} />
                </div>
                <CardTitle className="text-sm font-medium">{item.title}</CardTitle>
              </div>
              
              {/* Right side: Value */}
              <div className={`text-2xl font-bold ${useColoredCards ? item.color : ''}`}>
                {item.value}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
